import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useSelectedLocation } from "components";
import { createFormHelper } from "lib/Form";
import { useDefaultTemplatesQuery, useDeleteMediumMutation, useUploadEmailTemplateImageMutation } from "modules/CommunicationTemplates/hooks";
import { copyToClipboard, getFilesWithinSizeLimit } from "utils/common";

import type { CommunicationMedium } from "models";
import type { EmailMediumFormValues } from "modules/CommunicationTemplates/components";

type EmailTemplateEditorModalFormProps = {
  medium: CommunicationMedium;
  onClose: () => void;
};

const FormHelper = createFormHelper<EmailMediumFormValues>();

export const useEmailTemplateEditorModalForm = ({ onClose, medium }: EmailTemplateEditorModalFormProps) => {
  const { t } = useTranslation();

  const [fileUrl, setFileUrl] = useState<string>("");
  const [showDesign, setShowDesign] = useState(false);
  const [isDeleteTemplateModalOpen, setIsDeleteTemplateModalOpen] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { selectedLocation } = useSelectedLocation();
  const { setValue } = FormHelper.useFormContext();
  const { data: defaultTemplates } = useDefaultTemplatesQuery();
  const { mutateAsync: handleUploadFileAttachment, isPending: isUploadingAttachment } = useUploadEmailTemplateImageMutation();
  const { mutateAsync: deleteMedium, isPending: isDeletePending } = useDeleteMediumMutation();

  const handleToggleShowDesign = () => setShowDesign(!showDesign);
  const handleOpenDeleteModal = () => setIsDeleteTemplateModalOpen(true);
  const handleCloseDeleteModal = () => setIsDeleteTemplateModalOpen(false);

  const handleDeleteMedium = async () => {
    await deleteMedium(medium.communication_medium_id);
    onClose();
  };

  const revertToDefault = () => {
    const languageCode = selectedLocation?.language_code;
    const defaultTemplate = defaultTemplates?.find(
      temp => temp.language_code === languageCode && temp.event_type === medium.event_type && temp.channel === medium.channel
    );

    if (!defaultTemplate) return;

    setValue("subject", defaultTemplate.subject);
    setValue("message", defaultTemplate.message);
  };

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (iframe) {
      const contentWindow = iframe.contentWindow;

      if (contentWindow) {
        const contentHeight = contentWindow.document.body.scrollHeight;
        iframe.style.height = `${contentHeight + 10}px`;
      }
    }
  };

  const handleUploadAttachment = async (file: File) => {
    if (!selectedLocation) return;

    const extension = file.name.lastIndexOf(".") > -1 ? file.name.slice(file.name.lastIndexOf(".") + 1) : "unknown";
    const uploadFile = new File([file], file.name, { type: file.type || extension });

    const formData = new FormData();
    formData.append("location_id", String(selectedLocation.id));
    formData.append("file", uploadFile);

    const attachmentUrl = await handleUploadFileAttachment(formData);
    if (attachmentUrl) setFileUrl(attachmentUrl);
  };

  const handleFileDragOver = (evt: React.DragEvent<HTMLInputElement>) => evt.preventDefault();
  const handleFileChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    evt.preventDefault();
    getFilesWithinSizeLimit([...(evt.target.files ?? [])], t).forEach(file => handleUploadAttachment(file));
  };

  const handleFileDrop = (evt: React.DragEvent<HTMLInputElement>) => {
    evt.preventDefault();
    getFilesWithinSizeLimit([...evt.dataTransfer.files], t).forEach(file => handleUploadAttachment(file));
  };

  const handleCopyFileUrlClick = () => {
    copyToClipboard(fileUrl);
    toast.success(t("v8_copy_to_clipboard", "Copied to clipboard"));
  };

  return {
    fileUrl,
    iframeRef,
    showDesign,
    isDeletePending,
    isUploadingAttachment,
    isDeleteTemplateModalOpen,
    revertToDefault,
    handleFileDrop,
    handleFileChange,
    handleIframeLoad,
    handleFileDragOver,
    handleDeleteMedium,
    handleOpenDeleteModal,
    handleToggleShowDesign,
    handleCloseDeleteModal,
    handleCopyFileUrlClick
  };
};
